'use client';
import { H2, P, UniversalLink } from '@template/components';
import * as S from './styles.ts';
import InfoBlock from '@template/InfoBlock';
import { ImageProps } from '@template/components/Image';

interface FeaturesSectionComponentProps {
  heading?: string;
  subHeading?: string;
  column?: boolean;
  subHeadingCtaText?: { label: string; href?: string }[];
  altBackground?: boolean;
  largeFeatureIcons?: boolean;
  features?: {
    type?: 'block' | 'card';
    heading?: string;
    subHeading?: string;
    color?: 'primary' | 'secondary' | 'default';
    iconName: string;
    small?: boolean;
    cta?: {
      text: string;
      link: string;
      target?: '_blank';
    };
    filled?: boolean;
    image?: ImageProps;
    largeIcon?: boolean;
  }[];
}

//todo make sure to check which icons are being used here
const FeaturesSectionComponent = ({
  heading,
  features,
  altBackground,
  subHeading,
  subHeadingCtaText,
  column = false,
  largeFeatureIcons = false,
}: FeaturesSectionComponentProps) => {
  const paragraphs = subHeading?.split('\n');
  return (
    <S.FeaturesSectionWrapper $altBackground={altBackground}>
      <S.FeaturesSectionContainer $column={column}>
        <S.FeaturesTextContainer $column={column}>
          <H2>{heading}</H2>
          {subHeading && (
            <S.FeaturesSectionSubHeading>
              {paragraphs.map((paragraph, index) => (
                <P key={index}>{paragraph} </P>
              ))}
            </S.FeaturesSectionSubHeading>
          )}

          {subHeadingCtaText && (
            <S.FeaturesSectionSubHeading>
              {subHeadingCtaText.map((segment, index) =>
                segment.href ? (
                  <UniversalLink
                    key={index}
                    href={segment.href}
                    title={segment.label}
                  >
                    {segment.label}
                  </UniversalLink>
                ) : (
                  segment.label
                )
              )}
            </S.FeaturesSectionSubHeading>
          )}
        </S.FeaturesTextContainer>
        <S.FeaturesContentContainer $column={column}>
          {features?.map((feature, index) => (
            <InfoBlock
              largeIcon={feature.largeIcon || largeFeatureIcons}
              type={feature.type}
              key={index}
              heading={feature.heading}
              subHeading={feature.subHeading}
              iconName={feature.iconName}
              color={feature.color || 'primary'}
              cta={feature.cta}
              filled={feature.filled}
              small={feature.small}
              alignCenter={column}
              image={feature.image}
            />
          ))}
        </S.FeaturesContentContainer>
      </S.FeaturesSectionContainer>
    </S.FeaturesSectionWrapper>
  );
};

export default FeaturesSectionComponent;

export const options = [
  {
    description: '',
    displayName: 'FeaturesSectionComponent',
    methods: [],
    props: {
      heading: {
        required: false,
        flowType: { name: 'string' },
        description: '',
      },
      subHeading: {
        required: false,
        flowType: { name: 'string' },
        description: '',
      },
      column: {
        required: false,
        flowType: { name: 'boolean' },
        description: '',
        defaultValue: { value: 'false', computed: false },
      },
      subHeadingCtaText: {
        required: false,
        flowType: {
          name: 'Array',
          elements: [
            {
              name: 'signature',
              type: 'object',
              raw: '{ label: string; href?: string }',
              signature: {
                properties: [
                  {
                    key: 'label',
                    value: {
                      name: 'string',
                      required: true,
                    },
                  },
                  {
                    key: 'href',
                    value: {
                      name: 'string',
                      required: false,
                    },
                  },
                ],
              },
            },
          ],
          raw: '{ label: string; href?: string }[]',
        },
        description: '',
      },
      altBackground: {
        required: false,
        flowType: { name: 'boolean' },
        description: '',
      },
      features: {
        required: false,
        flowType: {
          name: 'Array',
          elements: [
            {
              name: 'signature',
              type: 'object',
              raw: '{\n  type?: "block" | "card";\n  heading?: string;\n  subHeading?: string;\n  color: "primary" | "secondary" | "default";\n  iconName: IconType;\n  small?: boolean;\n  cta?: {\n    text: string;\n    link: string;\n    target?: "_blank";\n  };\n  filled?: boolean;\n}',
              signature: {
                properties: [
                  {
                    key: 'type',
                    value: {
                      name: 'union',
                      raw: '"block" | "card"',
                      elements: [
                        {
                          name: 'literal',
                          value: '"block"',
                        },
                        {
                          name: 'literal',
                          value: '"card"',
                        },
                      ],
                      required: false,
                    },
                  },
                  {
                    key: 'heading',
                    value: {
                      name: 'string',
                      required: false,
                    },
                  },
                  {
                    key: 'subHeading',
                    value: {
                      name: 'string',
                      required: false,
                    },
                  },
                  {
                    key: 'color',
                    value: {
                      name: 'union',
                      raw: '"primary" | "secondary" | "default"',
                      elements: [
                        {
                          name: 'literal',
                          value: '"primary"',
                        },
                        {
                          name: 'literal',
                          value: '"secondary"',
                        },
                        {
                          name: 'literal',
                          value: '"default"',
                        },
                      ],
                      required: true,
                    },
                  },
                  {
                    key: 'iconName',
                    value: {
                      name: 'IconType',
                      required: true,
                    },
                  },
                  {
                    key: 'small',
                    value: {
                      name: 'boolean',
                      required: false,
                    },
                  },
                  {
                    key: 'cta',
                    value: {
                      name: 'signature',
                      type: 'object',
                      raw: '{\n  text: string;\n  link: string;\n  target?: "_blank";\n}',
                      signature: {
                        properties: [
                          {
                            key: 'text',
                            value: {
                              name: 'string',
                              required: true,
                            },
                          },
                          {
                            key: 'link',
                            value: {
                              name: 'string',
                              required: true,
                            },
                          },
                          {
                            key: 'target',
                            value: {
                              name: 'literal',
                              value: '"_blank"',
                              required: false,
                            },
                          },
                        ],
                      },
                      required: false,
                    },
                  },
                  {
                    key: 'filled',
                    value: {
                      name: 'boolean',
                      required: false,
                    },
                  },
                ],
              },
            },
          ],
          raw: '{\n  type?: "block" | "card";\n  heading?: string;\n  subHeading?: string;\n  color: "primary" | "secondary" | "default";\n  iconName: IconType;\n  small?: boolean;\n  cta?: {\n    text: string;\n    link: string;\n    target?: "_blank";\n  };\n  filled?: boolean;\n}[]',
        },
        description: '',
      },
    },
  },
];
